// src/pages/Orders/OrderForm/OrderFormSections/OrderAdjustmentsSection.jsx
import React, { useState } from "react";
import Button from "../../../../components/common/Button";
import Badge from "../../../../components/common/Badge";
import Modal from "../../../../components/common/Modal";
import Input from "../../../../components/common/Input";
import {
  FiPlus,
  FiPercent,
  FiTrash2,
  FiEdit3,
  FiAlertCircle,
  FiChevronDown,
  FiChevronUp,
  FiCheck,
  FiDollarSign,
  FiTag,
} from "react-icons/fi";

const OrderAdjustmentsSection = ({
  orderAdjustments = [],
  items = [],
  onAddAdjustment,
  onUpdateAdjustment,
  onRemoveAdjustment,
  formatCurrency,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({
    adjustmentType: "ITEM_DISCOUNT",
    scope: "ITEM",
    targetProductId: "",
    adjustment: {
      type: "PERCENTAGE",
      value: 0,
      overridePrice: 0,
    },
    reason: "",
    notes: "",
  });

  const handleOpenAddModal = () => {
    setFormData({
      adjustmentType: "ITEM_DISCOUNT",
      scope: "ITEM",
      targetProductId: items[0]?.productId || items[0]?.product?._id || "",
      adjustment: {
        type: "PERCENTAGE",
        value: 0,
        overridePrice: 0,
      },
      reason: "",
      notes: "",
    });
    setEditingIndex(null);
    setShowAddModal(true);
  };

  const handleOpenEditModal = (index) => {
    setFormData({ ...orderAdjustments[index] });
    setEditingIndex(index);
    setShowAddModal(true);
  };

  const handleSave = () => {
    // Validation
    if (!formData.reason.trim()) {
      alert("Please provide a reason for this adjustment");
      return;
    }

    if (formData.adjustment.type === "PERCENTAGE") {
      if (formData.adjustment.value <= 0 || formData.adjustment.value > 15) {
        alert("Percentage must be between 1 and 15");
        return;
      }
    } else if (formData.adjustment.type === "FIXED_AMOUNT") {
      if (formData.adjustment.value <= 0 || formData.adjustment.value > 5000) {
        alert("Fixed amount must be between 1 and 5000");
        return;
      }
    } else if (formData.adjustment.type === "OVERRIDE_PRICE") {
      if (formData.adjustment.overridePrice <= 0) {
        alert("Override price must be greater than 0");
        return;
      }
    }

    const adjustmentToSave = {
      ...formData,
      status: "ACTIVE",
      createdAt: new Date().toISOString(),
    };

    if (editingIndex !== null) {
      onUpdateAdjustment(editingIndex, adjustmentToSave);
    } else {
      onAddAdjustment(adjustmentToSave);
    }

    setShowAddModal(false);
  };

  const handleChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  // Calculate total impact
  const calculateTotalImpact = () => {
    // This is a simplified calculation - actual calculation would be in the pricing engine
    return orderAdjustments.reduce((total, adj) => {
      if (adj.status !== "ACTIVE") return total;
      if (adj.adjustment.type === "FIXED_AMOUNT") {
        return total + adj.adjustment.value;
      }
      return total;
    }, 0);
  };

  const totalImpact = calculateTotalImpact();

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <FiPercent className="w-5 h-5 text-orange-600" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-semibold text-gray-900">
              Order-Level Adjustments ({orderAdjustments.length})
            </h3>
            <p className="text-sm text-gray-600">
              Additional discounts or adjustments for this order
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {totalImpact > 0 && (
            <Badge variant="warning" size="sm">
              -{formatCurrency(totalImpact)}
            </Badge>
          )}
          {expanded ? (
            <FiChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <FiChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Content */}
      {expanded && (
        <div className="px-6 pb-6 border-t border-gray-200">
          <div className="mt-4 space-y-4">
            {orderAdjustments.length > 0 ? (
              <>
                {/* Adjustments List */}
                <div className="space-y-3">
                  {orderAdjustments.map((adjustment, index) => (
                    <OrderAdjustmentCard
                      key={index}
                      adjustment={adjustment}
                      index={index}
                      items={items}
                      onEdit={() => handleOpenEditModal(index)}
                      onRemove={() => onRemoveAdjustment(index)}
                      formatCurrency={formatCurrency}
                    />
                  ))}
                </div>

                {/* Add Another Button */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleOpenAddModal}
                  className="w-full"
                >
                  <FiPlus className="w-4 h-4 mr-2" />
                  Add Another Adjustment
                </Button>

                {/* Total Impact */}
                {totalImpact > 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900">
                        Total Order Adjustments Impact
                      </span>
                      <span className="text-2xl font-bold text-orange-600">
                        -{formatCurrency(totalImpact)}
                      </span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Empty State */
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <FiPercent className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-4">
                  No order-level adjustments added yet
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleOpenAddModal}
                >
                  <FiPlus className="w-4 h-4 mr-2" />
                  Add First Adjustment
                </Button>
              </div>
            )}

            {/* Info Banner */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex gap-2">
                <FiAlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">
                    Order-level adjustments apply on top of existing
                    cart/session adjustments
                  </p>
                  <p className="text-xs text-blue-700">
                    Maximum limits: 15% for percentage discounts and ₹5,000 for
                    fixed amount discounts. Price overrides have no limit.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Adjustment Modal */}
      <Modal
        isOpen={showAddModal}
        closeModal={() => setShowAddModal(false)}
        title={
          editingIndex !== null
            ? "Edit Order Adjustment"
            : "Add Order Adjustment"
        }
        maxWidth="2xl"
      >
        <div className="space-y-4">
          {/* Adjustment Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Adjustment Type *
            </label>
            <select
              value={formData.adjustmentType}
              onChange={(e) => handleChange("adjustmentType", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="ITEM_DISCOUNT">Item Discount</option>
              <option value="CART_DISCOUNT">Cart Discount</option>
              <option value="MAKING_CHARGES_WAIVER">
                Making Charges Waiver
              </option>
              <option value="EXCHANGE_BONUS">Exchange Bonus</option>
              <option value="LOYALTY_DISCOUNT">Loyalty Discount</option>
              <option value="PRICE_MATCH">Price Match</option>
              <option value="CUSTOM">Custom Adjustment</option>
            </select>
          </div>

          {/* Scope */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Scope *
            </label>
            <select
              value={formData.scope}
              onChange={(e) => handleChange("scope", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="ITEM">Specific Item</option>
              <option value="CART">Entire Cart</option>
              <option value="EXCHANGE_ITEM">Exchange Item</option>
            </select>
          </div>

          {/* Target Product (if scope is ITEM) */}
          {formData.scope === "ITEM" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Product *
              </label>
              <select
                value={formData.targetProductId}
                onChange={(e) =>
                  handleChange("targetProductId", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Select Product</option>
                {items.map((item) => (
                  <option
                    key={item.productId || item.product?._id}
                    value={item.productId || item.product?._id}
                  >
                    {item.product?.name || item.productSnapshot?.name} (
                    {item.productId || item.product?._id})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Adjustment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Adjustment Method *
            </label>
            <select
              value={formData.adjustment.type}
              onChange={(e) => handleChange("adjustment.type", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="PERCENTAGE">Percentage Discount</option>
              <option value="FIXED_AMOUNT">Fixed Amount Discount</option>
              <option value="OVERRIDE_PRICE">Override Price</option>
            </select>
          </div>

          {/* Value Input */}
          {formData.adjustment.type === "PERCENTAGE" && (
            <Input
              label="Discount Percentage *"
              type="number"
              min="1"
              max="15"
              step="0.1"
              value={formData.adjustment.value}
              onChange={(e) =>
                handleChange("adjustment.value", parseFloat(e.target.value))
              }
              placeholder="e.g., 10 for 10%"
              helperText="Maximum discount allowed: 15%"
            />
          )}

          {formData.adjustment.type === "FIXED_AMOUNT" && (
            <Input
              label="Discount Amount (₹) *"
              type="number"
              min="1"
              max="5000"
              step="1"
              value={formData.adjustment.value}
              onChange={(e) =>
                handleChange("adjustment.value", parseFloat(e.target.value))
              }
              placeholder="e.g., 500"
              helperText="Maximum discount allowed: ₹5,000"
            />
          )}

          {formData.adjustment.type === "OVERRIDE_PRICE" && (
            <Input
              label="Override Price (₹) *"
              type="number"
              min="1"
              step="1"
              value={formData.adjustment.overridePrice}
              onChange={(e) =>
                handleChange(
                  "adjustment.overridePrice",
                  parseFloat(e.target.value)
                )
              }
              placeholder="e.g., 15000"
              helperText="Set a custom price for this item"
            />
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason *
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) => handleChange("reason", e.target.value)}
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Explain why this adjustment is needed..."
              required
            />
          </div>

          {/* Notes (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Additional Notes (Optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Any additional context..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowAddModal(false)}
            >
              Cancel
            </Button>
            <Button type="button" variant="primary" onClick={handleSave}>
              {editingIndex !== null ? "Update Adjustment" : "Add Adjustment"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// Order Adjustment Card Component
const OrderAdjustmentCard = ({
  adjustment,
  index,
  items,
  onEdit,
  onRemove,
  formatCurrency,
}) => {
  const getAdjustmentValue = () => {
    if (adjustment.adjustment.type === "PERCENTAGE") {
      return `${adjustment.adjustment.value}% off`;
    } else if (adjustment.adjustment.type === "FIXED_AMOUNT") {
      return `${formatCurrency(adjustment.adjustment.value)} off`;
    } else if (adjustment.adjustment.type === "OVERRIDE_PRICE") {
      return `Override to ${formatCurrency(
        adjustment.adjustment.overridePrice
      )}`;
    }
    return "";
  };

  const getScopeIcon = () => {
    if (adjustment.scope === "ITEM") return "🎁";
    if (adjustment.scope === "CART") return "🛒";
    if (adjustment.scope === "EXCHANGE_ITEM") return "🔄";
    return "📝";
  };

  const getTargetProduct = () => {
    if (adjustment.scope !== "ITEM") return null;
    const item = items.find(
      (i) => (i.productId || i.product?._id) === adjustment.targetProductId
    );
    return (
      item?.product?.name || item?.productSnapshot?.name || "Unknown Product"
    );
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-2 flex-1">
          <span className="text-lg">{getScopeIcon()}</span>
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900 mb-1">
              {adjustment.adjustmentType?.replace(/_/g, " ") ||
                "Price Adjustment"}
            </h4>
            <p className="text-sm text-gray-600">{adjustment.reason}</p>
            {adjustment.scope === "ITEM" && (
              <p className="text-xs text-gray-500 mt-1">
                Target: {getTargetProduct()}
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-1 ml-3">
          <button
            type="button"
            onClick={onEdit}
            className="p-1.5 hover:bg-gray-100 rounded text-gray-600"
            title="Edit adjustment"
          >
            <FiEdit3 size={16} />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="p-1.5 hover:bg-red-50 rounded text-red-600"
            title="Remove adjustment"
          >
            <FiTrash2 size={16} />
          </button>
        </div>
      </div>

      {/* Adjustment Value */}
      <div className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg">
        <div className="flex items-center gap-2">
          <FiTag className="w-4 h-4 text-orange-600" />
          <span className="text-sm font-semibold text-orange-900">
            {getAdjustmentValue()}
          </span>
        </div>
        <Badge variant="success" size="sm">
          ACTIVE
        </Badge>
      </div>

      {/* Notes */}
      {adjustment.notes && (
        <p className="mt-2 text-xs text-gray-600 pt-2 border-t border-gray-200">
          {adjustment.notes}
        </p>
      )}
    </div>
  );
};

export default OrderAdjustmentsSection;
